import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Switch,
  FormControl,
  FormLabel,
  InputLabel,
  Typography,
  FormHelperText,
  Paper,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material'
import NoteAdd from '@mui/icons-material/NoteAdd'
import Note from '@mui/icons-material/Note'
import ExpandMore from '@mui/icons-material/ExpandMore'

const FormOverlay = React.memo(({ formSpec, onSubmit, canAddNotes, existingNotes, onSaveNote, messageIndex, onMinimize, disabled }) => {
  const [formData, setFormData] = useState(() => {
    // Initialize with default values
    const initialData = {}
    formSpec.fields?.forEach(field => {
      if (field.type === 'table') {
        initialData[field.id] = field.rows ? field.rows.map(r => ({ ...r })) : []
      } else if (field.default !== undefined) {
        initialData[field.id] = field.default
      } else if (field.type === 'checkbox' || field.type === 'multi-select') {
        initialData[field.id] = []
      } else if (field.type === 'radio') {
        initialData[field.id] = ''
      }
    })
    return initialData
  })

  const [errors, setErrors] = useState({})

  // Notes state (separate from form state to avoid interference)
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  const handleFieldChange = useCallback((fieldId, value) => {
    setFormData(prev => {
      const newData = { ...prev, [fieldId]: value }
      return newData
    })
    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[fieldId]) {
        const newErrors = { ...prev }
        delete newErrors[fieldId]
        return newErrors
      }
      return prev
    })
  }, [])

  const handleSubmit = useCallback(() => {
    const newErrors = {}

    formSpec.fields?.forEach(field => {
      const value = formData[field.id]

      // Table validation
      if (field.type === 'table') {
        if (field.required) {
          const rows = value || []
          const editableColumns = field.columns?.filter(c => c.editable) || []
          const hasEmpty = rows.some(row =>
            editableColumns.some(col => !row[col.id] || String(row[col.id]).trim() === '')
          )
          if (hasEmpty) {
            newErrors[field.id] = 'All cells must be filled in'
          }
        }
        return
      }

      // Required validation
      if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
        newErrors[field.id] = `${field.label} is required`
        return
      }

      // Skip other validations if field is empty and not required
      if (!value) return

      // Pattern validation
      if (field.validation?.pattern) {
        const regex = new RegExp(field.validation.pattern)
        if (!regex.test(value)) {
          newErrors[field.id] = field.validation.custom_message || 'Invalid format'
        }
      }

      // Length validation
      if (typeof value === 'string') {
        if (field.validation?.min_length && value.length < field.validation.min_length) {
          newErrors[field.id] = `Minimum ${field.validation.min_length} characters required`
        }
        if (field.validation?.max_length && value.length > field.validation.max_length) {
          newErrors[field.id] = `Maximum ${field.validation.max_length} characters allowed`
        }
      }

      // Numeric validation
      if (value && (field.type === 'number' || field.type === 'integer')) {
        const numValue = typeof value === 'string' ? parseFloat(value) : value
        if (field.validation?.min !== undefined && numValue < field.validation.min) {
          newErrors[field.id] = `Minimum value is ${field.validation.min}`
        }
        if (field.validation?.max !== undefined && numValue > field.validation.max) {
          newErrors[field.id] = `Maximum value is ${field.validation.max}`
        }
      }

      // Array validation (checkbox, multi-select)
      if (Array.isArray(value)) {
        if (field.validation?.min_selected && value.length < field.validation.min_selected) {
          newErrors[field.id] = `Select at least ${field.validation.min_selected} option(s)`
        }
        if (field.validation?.max_selected && value.length > field.validation.max_selected) {
          newErrors[field.id] = `Select at most ${field.validation.max_selected} option(s)`
        }
      }
    })

    if (Object.keys(newErrors).length === 0) {
      onSubmit({
        form_id: formSpec.form_id,
        form_spec: formSpec,  // Include the form spec in the submission
        status: 'submitted',
        values: formData
      })
    } else {
      setErrors(newErrors)
    }
  }, [formSpec, formData, onSubmit])

  // Notes handlers (separate from form logic)
  const handleToggleNotes = useCallback(() => {
    setNotesExpanded(prev => !prev)
  }, [])

  const handleNoteTextChange = useCallback((e) => {
    setNoteText(e.target.value)
  }, [])

  const handleSaveNote = useCallback(async () => {
    if (!noteText.trim() || messageIndex === null) return

    setSavingNote(true)
    try {
      await onSaveNote(messageIndex, noteText.trim())
      setNoteText('')
      setNotesExpanded(false)
    } catch (error) {
      console.error('Failed to save note:', error)
    } finally {
      setSavingNote(false)
    }
  }, [noteText, messageIndex, onSaveNote])

  const getInitialValue = (field) => {
    if (formData[field.id] !== undefined) {
      return formData[field.id]
    }
    if (field.type === 'table') {
      return field.rows ? field.rows.map(r => ({ ...r })) : []
    }
    if (field.default !== undefined) {
      return field.default
    }
    if (field.type === 'checkbox' || field.type === 'multi-select') {
      return []
    }
    // For radio buttons, return empty string to allow proper selection
    if (field.type === 'radio') {
      return ''
    }
    return ''
  }

  const renderField = (field) => {
    const value = getInitialValue(field)
    const error = errors[field.id]

    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel':
        return (
          <TextField
            fullWidth
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
          />
        )

      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
          />
        )

      case 'number':
      case 'integer':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => {
              const val = field.type === 'integer'
                ? parseInt(e.target.value) || ''
                : parseFloat(e.target.value) || ''
              handleFieldChange(field.id, val)
            }}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
              step: field.validation?.step || (field.type === 'integer' ? 1 : 0.01)
            }}
          />
        )

      case 'date':
      case 'datetime':
      case 'time':
        return (
          <TextField
            fullWidth
            type={field.type === 'datetime' ? 'datetime-local' : field.type}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        )

      case 'select':
        return (
          <FormControl fullWidth required={field.required} error={!!error} size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {(error || field.description) && (
              <FormHelperText>{error || field.description}</FormHelperText>
            )}
          </FormControl>
        )

      case 'radio':
        return (
          <FormControl required={field.required} error={!!error}>
            <FormLabel>{field.label}</FormLabel>
            <RadioGroup
              name={field.id}
              value={value || ''}
              onChange={(e) => {
                handleFieldChange(field.id, e.target.value)
              }}
            >
              {field.options?.map(option => {
                const optionValue = String(option.value)
                return (
                  <FormControlLabel
                    key={option.value}
                    value={optionValue}
                    control={<Radio />}
                    label={option.label}
                  />
                )
              })}
            </RadioGroup>
            {(error || field.description) && (
              <FormHelperText>{error || field.description}</FormHelperText>
            )}
          </FormControl>
        )

      case 'checkbox':
      case 'multi-select':
        return (
          <FormControl required={field.required} error={!!error}>
            <FormLabel>{field.label}</FormLabel>
            <FormGroup>
              {field.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={value.includes?.(option.value) || false}
                      onChange={(e) => {
                        const currentValue = value || []
                        const newValue = e.target.checked
                          ? [...currentValue, option.value]
                          : currentValue.filter(v => v !== option.value)
                        handleFieldChange(field.id, newValue)
                      }}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {(error || field.description) && (
              <FormHelperText>{error || field.description}</FormHelperText>
            )}
          </FormControl>
        )

      case 'toggle':
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value || false}
                onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              />
            }
            label={field.label}
          />
        )

      case 'table': {
        const rows = value || []
        const columns = field.columns || []
        const editableColumns = columns.filter(c => c.editable)
        const handleCellChange = (rowIndex, colId, cellValue) => {
          const updated = rows.map((row, i) =>
            i === rowIndex ? { ...row, [colId]: cellValue } : row
          )
          handleFieldChange(field.id, updated)
        }
        return (
          <Box>
            {field.label && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {field.label}
                {field.required && <span style={{ color: 'var(--mui-palette-error-main, #d32f2f)' }}> *</span>}
              </Typography>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.id}
                        style={{
                          textAlign: 'left',
                          padding: '6px 10px',
                          borderBottom: '2px solid #e0e0e0',
                          whiteSpace: 'nowrap',
                          color: 'rgba(0,0,0,0.6)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {col.label}
                        {col.editable && field.required && <span style={{ color: '#d32f2f' }}> *</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      style={{ backgroundColor: rowIndex % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.025)' }}
                    >
                      {columns.map(col => (
                        <td
                          key={col.id}
                          style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}
                        >
                          {col.editable ? (
                            <input
                              type="text"
                              value={row[col.id] ?? ''}
                              onChange={e => handleCellChange(rowIndex, col.id, e.target.value)}
                              style={{
                                border: 'none',
                                borderBottom: '1px solid #bdbdbd',
                                outline: 'none',
                                background: 'transparent',
                                fontSize: '0.875rem',
                                padding: '2px 4px',
                                width: '100%',
                                minWidth: 120,
                                fontFamily: 'inherit',
                              }}
                              onFocus={e => { e.target.style.borderBottomColor = '#1976d2' }}
                              onBlur={e => { e.target.style.borderBottomColor = '#bdbdbd' }}
                            />
                          ) : (
                            <span style={{ padding: '2px 4px', display: 'block' }}>{row[col.id] ?? ''}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {error && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {error}
              </Typography>
            )}
          </Box>
        )
      }

      default:
        return (
          <Typography color="error">
            Unsupported field type: {field.type}
          </Typography>
        )
    }
  }

  if (!formSpec) return null

  return (
      <Paper
        sx={{
          mb: 2, // Margin bottom to separate from input field
          maxHeight: '60vh',
          overflowY: 'auto',
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {formSpec.title}
            </Typography>
            {onMinimize && (
              <IconButton onClick={onMinimize} size="small">
                <ExpandMore />
              </IconButton>
            )}
          </Box>

          {formSpec.description && (
            <Typography variant="body2" color="text.secondary" paragraph>
              {formSpec.description}
            </Typography>
          )}

          {/* Notes section - moved to top */}
          {canAddNotes && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconButton
                  onClick={handleToggleNotes}
                  size="small"
                  sx={{
                    backgroundColor: (existingNotes?.length > 0) ? '#FFC107' : 'rgba(255, 255, 255, 0.9)',
                    color: (existingNotes?.length > 0) ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.6)',
                    width: 28,
                    height: 28,
                    border: (existingNotes?.length > 0) ? 'none' : '2px solid rgba(0, 0, 0, 0.3)',
                    '&:hover': {
                      backgroundColor: (existingNotes?.length > 0) ? '#FFB300' : 'rgba(255, 255, 255, 1)',
                      border: (existingNotes?.length > 0) ? 'none' : '2px solid rgba(0, 0, 0, 0.5)',
                    },
                  }}
                >
                  {(existingNotes?.length > 0) ? <Note sx={{ fontSize: '1rem' }} /> : <NoteAdd sx={{ fontSize: '1rem' }} />}
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                  {notesExpanded ? 'Add a note' : 'Notes'}
                  {existingNotes?.length > 0 && ` (${existingNotes.length})`}
                </Typography>
              </Box>

              <Collapse in={notesExpanded}>
                <Box sx={{ mt: 2 }}>
                  {/* Display existing notes */}
                  {existingNotes?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {existingNotes.map((note, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            bgcolor: 'rgba(255, 193, 7, 0.1)',
                            borderRadius: 1,
                            borderLeft: '3px solid #FFC107',
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {note.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {note.created_by} • {new Date(note.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Add new note */}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a note..."
                    value={noteText}
                    onChange={handleNoteTextChange}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={handleToggleNotes}
                      disabled={savingNote}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveNote}
                      disabled={!noteText.trim() || savingNote}
                    >
                      {savingNote ? 'Saving...' : 'Save Note'}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
            {formSpec.fields?.map(field => (
              <Box key={field.id}>
                {renderField(field)}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={disabled}
            >
              {formSpec.submit_label || 'Submit'}
            </Button>
          </Box>
        </Box>
      </Paper>
  )
})

FormOverlay.displayName = 'FormOverlay'

export default FormOverlay
